import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { database, ref, get, remove } from '../firebase';
import { 
  FiImage, FiVideo, FiTrash2, FiShare2, FiEdit, 
  FiClock, FiEye, FiPlusCircle, FiSearch, FiX,
  FiUpload
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const MyMedia = () => {
  const [media, setMedia] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState({});
  const [shareMessage, setShareMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadOptions, setShowUploadOptions] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMedia = async () => {
      const userData = JSON.parse(localStorage.getItem('currentUser'));
      if (!userData || !userData.mobile) {
        setError('No logged-in user found. Please log in to view your media.');
        setLoading(false);
        return;
      }

      const sanitizedMobile = userData.mobile.replace(/[^0-9]/g, '_');

      try {
        const allMediaItems = [];

        // Fetch videos
        const videosRef = ref(database, `videoplayer/videoUpload/${sanitizedMobile}`);
        const videosSnapshot = await get(videosRef);
        if (videosSnapshot.exists()) {
          const videoData = videosSnapshot.val();
          Object.entries(videoData).forEach(([videoId, video]) => {
            if (video.status !== 'approved') return;
            allMediaItems.push({
              id: videoId,
              type: 'video',
              title: video.title || 'Untitled Video',
              description: video.description || '',
              thumbnail: video.thumbnailURL || 'https://via.placeholder.com/320x180?text=No+Thumbnail',
              videoURL: video.videoURL || '',
              views: video.views || 0,
              uploadDate: video.uploadDate ? new Date(video.uploadDate) : new Date(),
              category: video.category || 'Uncategorized',
            });
          });
        }

        // Fetch photos
        const photosRef = ref(database, `videoplayer/photoUpload/${sanitizedMobile}`);
        const photosSnapshot = await get(photosRef);
        if (photosSnapshot.exists()) {
          const photoData = photosSnapshot.val();
          Object.entries(photoData).forEach(([photoId, photo]) => {
            if (photo.status !== 'approved') return;
            allMediaItems.push({
              id: photoId,
              type: 'photo',
              title: photo.title || 'Untitled Photo',
              thumbnail: photo.photoURL || 'https://via.placeholder.com/320x180?text=No+Thumbnail',
              photoURL: photo.photoURL || '',
              uploadDate: photo.uploadDate ? new Date(photo.uploadDate) : new Date(),
              category: photo.category || 'Uncategorized',
            });
          });
        }

        // Sort by upload date (newest first)
        allMediaItems.sort((a, b) => b.uploadDate - a.uploadDate);
        setMedia(allMediaItems);
      } catch (err) {
        console.error('Error fetching media:', err);
        setError('Failed to load media. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchMedia();
  }, []);

  const filteredMedia = media.filter(item => 
    item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDelete = async (mediaItem) => {
    const userData = JSON.parse(localStorage.getItem('currentUser'));
    if (!userData || !userData.mobile) {
      setError('Please log in to delete media.');
      return;
    }

    const sanitizedMobile = userData.mobile.replace(/[^0-9]/g, '_');
    setDeleteLoading((prev) => ({ ...prev, [mediaItem.id]: true }));

    try {
      const mediaRef = ref(database, `videoplayer/${mediaItem.type}Upload/${sanitizedMobile}/${mediaItem.id}`);
      await remove(mediaRef);
      setMedia((prevMedia) => prevMedia.filter((item) => item.id !== mediaItem.id));
    } catch (err) {
      console.error(`Error deleting ${mediaItem.type}:`, err);
      setError(`Failed to delete ${mediaItem.type}. Please try again.`);
      setTimeout(() => setError(''), 3000);
    } finally {
      setDeleteLoading((prev) => ({ ...prev, [mediaItem.id]: false }));
    }
  };

  const handleShare = (mediaItem) => {
    const shareUrl = `${window.location.origin}/${mediaItem.type}/${mediaItem.id}`;
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        setShareMessage('Link copied to clipboard!');
        setTimeout(() => setShareMessage(''), 3000);
      })
      .catch(() => {
        setError('Failed to copy link.');
        setTimeout(() => setError(''), 3000);
      });
  };

  const handleEdit = (mediaItem) => {
    navigate(`/edit/${mediaItem.id}`, { state: { mediaType: mediaItem.type } });
  };


const handleUploadOptionClick = (type) => {
    setShowUploadOptions(false);
    
    // Using a slight delay for smoother modal close animation
    setTimeout(() => {
      if (type === 'video') {
        navigate('/upload'); // Updated route for video upload
      } else if (type === 'photo') {
        navigate('/photo-upload'); // Updated route for photo upload
      }
    }, 300);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50 flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="flex justify-center mb-4">
            <motion.div 
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
              className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full"
            />
          </div>
          <h2 className="text-xl font-semibold text-indigo-700">Loading your media...</h2>
          <p className="text-indigo-400 mt-2">Please wait while we fetch your content</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50 p-4 sm:p-8 relative">
      {/* Upload Options Modal */}
      <AnimatePresence>
        {showUploadOptions && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowUploadOptions(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-semibold text-gray-800">Upload New Media</h3>
                <button 
                  onClick={() => setShowUploadOptions(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <FiX size={24} />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleUploadOptionClick('video')}
                  className="flex flex-col items-center justify-center p-6 rounded-lg border-2 border-blue-100 hover:border-blue-300 bg-blue-50 hover:bg-blue-100 transition-all"
                >
                  <div className="bg-blue-100 p-4 rounded-full mb-4">
                    <FiVideo className="text-blue-600 text-2xl" />
                  </div>
                  <h4 className="font-medium text-blue-800">Upload Video</h4>
                  <p className="text-sm text-blue-600 mt-1 text-center">Share your videos with the community</p>
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleUploadOptionClick('photo')}
                  className="flex flex-col items-center justify-center p-6 rounded-lg border-2 border-indigo-100 hover:border-indigo-300 bg-indigo-50 hover:bg-indigo-100 transition-all"
                >
                  <div className="bg-indigo-100 p-4 rounded-full mb-4">
                    <FiImage className="text-indigo-600 text-2xl" />
                  </div>
                  <h4 className="font-medium text-indigo-800">Upload Photo</h4>
                  <p className="text-sm text-indigo-600 mt-1 text-center">Share your photos with the community</p>
                </motion.button>
              </div>
              
              <div className="p-4 bg-gray-50 text-center">
                <button 
                  onClick={() => setShowUploadOptions(false)}
                  className="text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-7xl mx-auto"
      >
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
              <span className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white p-2 rounded-lg">
                <FiVideo className="inline" />
              </span>
              My Media Library
            </h1>
            <p className="text-gray-500 mt-2">Manage your uploaded photos and videos</p>
          </div>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowUploadOptions(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-5 py-3 rounded-lg shadow-md hover:shadow-lg transition-all hover:from-indigo-700 hover:to-blue-700"
          >
            <FiPlusCircle className="text-lg" />
            <span>Upload New</span>
          </motion.button>
        </div>

        {/* Search and Filter */}
        <div className="mb-8 relative">
          <div className="relative max-w-xl">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by title or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent shadow-sm"
            />
          </div>
        </div>

        {/* Status Messages */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 border border-red-200 flex items-start gap-3"
            >
              <div className="bg-red-100 p-1 rounded-full">
                <FiTrash2 className="text-red-600" />
              </div>
              <div>
                <p className="font-medium">{error}</p>
              </div>
            </motion.div>
          )}
          {shareMessage && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="bg-indigo-50 text-indigo-600 p-4 rounded-lg mb-6 border border-indigo-200 flex items-start gap-3"
            >
              <div className="bg-indigo-100 p-1 rounded-full">
                <FiShare2 className="text-indigo-600" />
              </div>
              <div>
                <p className="font-medium">{shareMessage}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Media Grid */}
        {filteredMedia.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-16 bg-white rounded-xl shadow-sm"
          >
            <div className="bg-indigo-100 p-6 rounded-full mb-6">
              <FiImage className="text-indigo-600 text-4xl" />
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No media found</h3>
            <p className="text-gray-500 max-w-md text-center mb-6">
              {searchQuery 
                ? "No items match your search. Try different keywords."
                : "You haven't uploaded any media yet. Get started by uploading your first photo or video!"}
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowUploadOptions(true)}
              className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all"
            >
              <FiUpload className="text-lg" />
              <span>Upload Media</span>
            </motion.button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <AnimatePresence>
              {filteredMedia.map((item) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <Link to={`/${item.type}/${item.id}`} className="block">
                    <div className="relative pt-[56.25%] overflow-hidden">
                      <img
                        src={item.thumbnail}
                        alt={item.title}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                        onError={(e) => (e.target.src = 'https://via.placeholder.com/320x180?text=No+Thumbnail')}
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                        <h3 className="text-white font-semibold line-clamp-2">{item.title}</h3>
                      </div>
                      <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm text-xs font-semibold px-2 py-1 rounded-full flex items-center shadow-sm">
                        {item.type === 'video' ? (
                          <>
                            <FiVideo className="mr-1 text-blue-600" />
                            <span className="text-gray-700">Video</span>
                          </>
                        ) : (
                          <>
                            <FiImage className="mr-1 text-indigo-600" />
                            <span className="text-gray-700">Photo</span>
                          </>
                        )}
                      </div>
                    </div>
                  </Link>

                  <div className="p-4">
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center text-sm text-gray-500">
                        <FiClock className="mr-1" />
                        <span>{item.uploadDate.toLocaleDateString()}</span>
                      </div>
                      {item.type === 'video' && (
                        <div className="flex items-center text-sm text-gray-500">
                          <FiEye className="mr-1" />
                          <span>{item.views || 0} views</span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <motion.button
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleEdit(item)}
                        className="flex-1 min-w-[100px] text-sm px-3 py-2 rounded-lg bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors duration-200 flex items-center justify-center gap-2"
                      >
                        <FiEdit size={16} />
                        Edit
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleShare(item)}
                        className="flex-1 min-w-[100px] text-sm px-3 py-2 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors duration-200 flex items-center justify-center gap-2"
                      >
                        <FiShare2 size={16} />
                        Share
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleDelete(item)}
                        disabled={deleteLoading[item.id]}
                        className={`flex-1 min-w-[100px] text-sm px-3 py-2 rounded-lg ${
                          deleteLoading[item.id]
                            ? 'bg-red-100 text-red-400 cursor-not-allowed'
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                        } transition-colors duration-200 flex items-center justify-center gap-2`}
                      >
                        <FiTrash2 size={16} />
                        {deleteLoading[item.id] ? 'Deleting...' : 'Delete'}
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default MyMedia;